import { APP_ROUTES } from '@/common/constants'
import { KYC_INIT_TARGET } from '@/kyc/kycFlowResolver'

export const FRANKIE_ONE_FLOW_CONTEXT = {
    DEFAULT: 'DEFAULT',
    ADVISER_CLIENT: 'ADVISER_CLIENT',
} as const

export type FrankieOneFlowContext = (typeof FRANKIE_ONE_FLOW_CONTEXT)[keyof typeof FRANKIE_ONE_FLOW_CONTEXT]

type ResolveFlowContextParams = {
    pathname: string
    target: (typeof KYC_INIT_TARGET)[keyof typeof KYC_INIT_TARGET]
}

export function resolveFrankieOneFlowContext({ pathname, target }: ResolveFlowContextParams): FrankieOneFlowContext {
    if (pathname === APP_ROUTES.APPLICATION_KYC && target === KYC_INIT_TARGET.ACCOUNT_PARTICIPANT) {
        return FRANKIE_ONE_FLOW_CONTEXT.ADVISER_CLIENT
    }

    return FRANKIE_ONE_FLOW_CONTEXT.DEFAULT
}

export function getWelcomeScreenConfig(context: FrankieOneFlowContext) {
    if (context === FRANKIE_ONE_FLOW_CONTEXT.ADVISER_CLIENT) {
        return {
            name: 'WELCOME' as const,
            type: 'manual' as const,
            title: { label: "Let's verify your client's identity" },
            descriptions: [
                {
                    label: "You are completing this verification on behalf of your client. All fields must be filled and entered exactly as they appear on your client's ID.",
                },
            ],
            instructions: {
                label: 'You will need',
                content: [
                    { label: 'A few mins to complete this application' },
                    {
                        label: "Your client's ID details (this could be a driver's licence, passport or Medicare card)",
                    },
                    { label: 'Your client must be over 18 years of age' },
                    {
                        label: 'Your client must be an Australian citizen, permanent resident or Visa holder.',
                    },
                ],
            },
            cta: { label: 'Start identity verification' },
        }
    }

    return {
        name: 'WELCOME' as const,
        type: 'manual' as const,
    }
}

export function getConsentScreenConfig(context: FrankieOneFlowContext) {
    if (context === FRANKIE_ONE_FLOW_CONTEXT.ADVISER_CLIENT) {
        return {
            name: 'CONSENT' as const,
            title: { label: 'Your consent matters' },
            descriptions: [
                {
                    label: "Your client's personal information is being collected and used in accordance with SIX's Privacy Policy.",
                },
                {
                    label: "In addition, for the purpose of verifying your client's identity, you consent to: a) verification of your client's personal information with credit bureau header files (for verification only); b) verification against records held by official document issuers or official record holders via third party systems; and c) verification agent(s) acting as a nominated intermediary in accordance with Australian Privacy Principles.",
                },
                {
                    label: "You consent to the use by third parties of the results of any verification checks on your client's identity for the purposes of monitoring and improving their verification services.",
                },
            ],
            checkbox: {
                label: 'I confirm I am authorised to provide this consent on behalf of my client.',
            },
        }
    }

    return {
        name: 'CONSENT' as const,
        title: { label: 'Your consent matters' },
        descriptions: [
            {
                label: "Your personal information is being collected and used in accordance with SIX's Privacy Policy.",
            },
            {
                label: 'In addition, for the purposes of verifying your identity, you consent to: a) the verification of your personal information with credit bureau header files (for verification only); b) verification against records held by official document issuers or official record holders via third party systems; and c) verification agent(s) acting as a nominated intermediary in accordance with Australian Privacy Principles.',
            },
            {
                label: 'You consent to the use by third parties of the results of any verification checks on your identity for the purposes of monitoring and improving their verification services.',
            },
        ],
        checkbox: {
            label: 'I have read and agree to the above disclosure',
        },
    }
}

export function getPersonalScreenConfig(context: FrankieOneFlowContext) {
    if (context === FRANKIE_ONE_FLOW_CONTEXT.ADVISER_CLIENT) {
        return {
            name: 'PERSONAL' as const,
            type: 'manual' as const,
            title: { label: 'Client personal details' },
            descriptions: [
                {
                    label: "The details you provide below will be used to verify your client's identity - make sure they match your client's ID documents.",
                },
            ],
        }
    }

    return {
        name: 'PERSONAL' as const,
        type: 'manual' as const,
    }
}

export function getDocumentScreenConfig(context: FrankieOneFlowContext) {
    if (context === FRANKIE_ONE_FLOW_CONTEXT.ADVISER_CLIENT) {
        return {
            name: 'DOCUMENT' as const,
            type: 'manual' as const,
            numberOfIDs: 1,
            title: { label: "Select your client's ID" },
            descriptions: [
                {
                    label: "We need one form of ID document to verify your client's identity.",
                },
            ],
            additionalInstructions: [{ label: "Your client's ID must be valid and not expired" }],
        }
    }

    return {
        name: 'DOCUMENT' as const,
        type: 'manual' as const,
        numberOfIDs: 1,
    }
}

export function getResultFailScreenConfig(context: FrankieOneFlowContext) {
    if (context === FRANKIE_ONE_FLOW_CONTEXT.ADVISER_CLIENT) {
        return {
            name: 'RESULT' as const,
            type: 'manual' as const,
            state: 'FAIL' as const,
            title: { label: 'Maximum attempts reached' },
            descriptions: [
                {
                    label: "We were unable to verify your client's identity. Please contact support if you need further assistance.",
                },
            ],
            cta: { label: 'Close' },
        }
    }

    return {
        name: 'RESULT' as const,
        type: 'manual' as const,
        state: 'FAIL' as const,
        title: { label: 'Maximum attempts reached' },
        descriptions: [
            {
                label: 'We were unable to verify your identity. Please contact support if you need further assistance.',
            },
        ],
        cta: { label: 'Close' },
    }
}
